import type { ReactNode } from 'react';
import type { LoginKitMessages } from './i18n';

export type NoticeTone = 'error' | 'success' | 'info';

export interface NoticeProps {
  tone: NoticeTone;
  /** Título curto em negrito. Opcional. */
  title?: ReactNode;
  /** Corpo da mensagem. */
  children: ReactNode;
  /** Se fornecido, exibe o botão de fechar. */
  onDismiss?: () => void;
  /** Rótulo acessível do botão de fechar. */
  dismissLabel?: string;
}

const ICONS: Record<NoticeTone, ReactNode> = {
  error: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.5v5" />
      <path d="M12 16h.01" />
    </>
  ),
  success: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M8.5 12.2l2.3 2.3 4.7-4.9" />
    </>
  ),
  info: (
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 11v5" />
      <path d="M12 8h.01" />
    </>
  ),
};

/**
 * Banner de feedback (erro / sucesso / info) — COMPARTILHADO por todos os
 * portais. Usa as cores semânticas do design system; o accent de cada portal
 * cascateia naturalmente pelos tokens, então a identidade é preservada sem
 * abrir mão da legibilidade semântica (erro sempre lê como erro).
 *
 * Acessível: `role="alert"` + `aria-live="assertive"` para erros (anúncio
 * imediato), `role="status"` + `aria-live="polite"` para sucesso/info.
 */
export function Notice({ tone, title, children, onDismiss, dismissLabel }: NoticeProps) {
  const isError = tone === 'error';
  return (
    <div
      className={`login-notice login-notice--${tone}`}
      role={isError ? 'alert' : 'status'}
      aria-live={isError ? 'assertive' : 'polite'}
    >
      <span className="login-notice__icon" aria-hidden="true">
        <svg viewBox="0 0 24 24">{ICONS[tone]}</svg>
      </span>
      <div className="login-notice__body">
        {title && <span className="login-notice__title">{title}</span>}
        <span className="login-notice__text">{children}</span>
      </div>
      {onDismiss && (
        <button type="button" className="login-notice__dismiss" onClick={onDismiss} aria-label={dismissLabel}>
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
      )}
    </div>
  );
}

/**
 * Traduz um erro lançado por um handler de auth numa mensagem amigável.
 *
 * Mapeia por status HTTP (duck-typing em `.status`, sem acoplar o login-kit ao
 * cliente BFF) e por falha de rede (`TypeError` do `fetch`). Se o handler lançar
 * um `Error` com mensagem própria, ela é preservada; caso contrário, usa o
 * fallback informado.
 */
export function describeAuthError(
  error: unknown,
  messages: LoginKitMessages['notice'],
  fallback: string,
): string {
  // O código vem antes do status: um 401 de "precisa definir senha nova" não é credencial errada, e
  // dizer que é manda a pessoa conferir de novo algo que está certo.
  const code = (error as { message?: unknown } | null | undefined)?.message;
  if (code === 'sign_in_needs_new_password') return messages.needsNewPassword;
  if (code === 'sign_in_password_not_set') return messages.passwordNotSet;
  if (code === 'sign_in_needs_second_factor') return messages.needsSecondFactor;
  if (code === 'sign_in_already_signed_in') return messages.alreadySignedIn;
  if (code === 'magic_link_client_mismatch') return messages.magicLinkClientMismatch;
  if (code === 'magic_link_expired') return messages.magicLinkExpired;

  const status = (error as { status?: unknown } | null | undefined)?.status;
  if (typeof status === 'number') {
    if (status === 400 || status === 401 || status === 403) return messages.invalidCredentials;
    if (status === 429) return messages.tooManyAttempts;
    if (status >= 500) return messages.serverError;
  }

  // Falha de rede do fetch (offline, CORS, DNS) chega como TypeError.
  if (error instanceof TypeError) return messages.networkError;

  return fallback;
}
